<template>
  <div class="p-3 t-wrapper" style="min-height: 100%">
    <!-- Category pick -->
    <div
      v-if="screen === 'category'"
      class="container w-100 h-100 category-screen"
    >
      <div
        class="w-100 h-100 d-flex flex-column align-items-center justify-content-center py-4"
      >
        <div class="category-header">
          <span class="category-star">✦</span>
          <h3 class="mm-font text-center">တားရော့ ကံကြမ္မာ</h3>
          <span class="category-star category-star-delay">✦</span>
        </div>
        <div class="pane-line"></div>
        <h5 class="text-center mt-4 mb-4 mm-font">
          မေးလိုသော အကြောင်းအရာ တစ်ခုကို ရွေးချယ်ပါ
        </h5>
        <div class="container">
          <div class="row justify-content-center">
            <div
              v-for="(cat, idx) in categories"
              :key="cat.id"
              class="col-6 mb-3"
            >
              <div
                class="category-card mm-font"
                :style="{ animationDelay: idx * 60 + 'ms' }"
                role="button"
                tabindex="0"
                @click="pickCategory(cat)"
                @keyup.enter="pickCategory(cat)"
              >
                <span class="category-icon" :class="categoryTint(cat.id)">{{ categoryIcon(cat.id) }}</span>
                <span class="category-label">{{ cat.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fan spread -->
    <div v-else-if="screen === 'fan'" class="container w-100">
      <div class="d-flex flex-column align-items-center pt-2">
        <h3 class="mm-font text-center mb-1">{{ selectedCategory.name }}</h3>
        <div class="pane-line"></div>
        <h5 class="mm-font text-center mt-3 mb-2">ကဒ်တစ်ခုကို ရွေးချယ်ပါ</h5>
      </div>
      <div class="tarot-stage" ref="stage">
        <div
          v-for="card in fanCards"
          :key="card.id"
          class="tarot-card-wrap"
          :class="{ shuffling: !spread, dealing: spread && !cascadeCleared }"
          :style="{
            left: (spread ? card.x : card.gatherX) + 'px',
            top: (spread ? card.y : card.gatherY) + 'px',
            zIndex: 5,
            transitionDelay:
              (spread && !cascadeCleared ? card.stagger : 0) + 'ms',
            transform: 'rotate(' + (spread ? card.arc : 0) + 'deg)',
          }"
          @click="selectCard(card)"
        >
          <div
            class="tarot-card-deal"
            :style="{
              animationDelay:
                (spread && !cascadeCleared ? card.stagger : 0) + 'ms',
            }"
          >
            <img :src="cardBack" alt="Card back" class="tarot-card-img" />
          </div>
        </div>
      </div>
    </div>

    <!-- Reveal: face-down card + 5s progress -->
    <div
      v-else-if="screen === 'reveal'"
      ref="revealScreen"
      class="container reveal-screen"
      :style="{ marginTop: revealTopOffset + 'px' }"
    >
      <h3 class="mm-font text-center mb-3">{{ selectedCategory.name }}</h3>
      <div class="reveal-card">
        <img :src="cardBack" alt="Card back" />
      </div>
      <p class="mm-font reveal-hint">သင့်ကံကြမ္မာကို ဖတ်ကြားနေပါသည်…</p>
      <div class="reveal-progress">
        <div
          ref="progressBar"
          class="reveal-progress-bar"
          :class="{ active: revealProgress }"
        ></div>
      </div>
    </div>

    <!-- Result (always mounted; kept hidden while the reveal plays so the
         long reading text is already laid out + painted, then shown at once) -->
    <div
      class="container h-100 result-screen"
      :class="{ 'result-screen--active': screen === 'result' }"
    >
      <div class="w-100 h-100 d-flex flex-column align-items-center pb-4">
        <template v-if="result">
          <img
            :src="result.card.image"
            class="tarot-card"
            :alt="result.card.name"
          />
          <h3 class="mm-font text-center mt-3 mb-1" style="font-weight: bold">
            {{ result.card.name }}
          </h3>
          <h5 class="mm-font text-center mb-2">{{ selectedCategory.name }}</h5>
          <div class="container">
            <div class="row">
              <div class="col-lg-12">
                <p class="mm-font mt-2 result-label">ကဒ်အဓိပ္ပါယ်</p>
                <p class="mm-font result-text" style="text-align: justify">
                  {{ result.card.description }}
                </p>
                <p class="mm-font mt-3 result-label">မိမိအတွက် ဖတ်ကြားချက်</p>
                <p
                  class="mm-font result-text"
                  style="text-align: justify; word-break: break-word"
                >
                  {{ result.meaning }}
                </p>
              </div>
            </div>
          </div>
          <span class="mt-3 back-btn" @click="back">&#8592; back</span>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {
  TAROT_CATEGORIES,
  TAROT_CARDS,
  TAROT_MEANINGS,
  CARD_BACK,
} from "@/utils/tarotData";

export default {
  name: "TarotShuffle",

  data() {
    return {
      categories: TAROT_CATEGORIES,
      cardBack: CARD_BACK,
      screen: "category",
      selectedCategory: null,
      fanCards: [],
      spread: false,
      cascadeCleared: false,
      selectedCard: null,
      isDrawing: false,
      revealProgress: false,
      revealTopOffset: 0,
      result: null,
      spreadTimeout: null,
      cascadeTimeout: null,
      revealTimeout: null,
    };
  },

  beforeRouteLeave(to, from, next) {
    this.$swal({
      title: "ထွက်ခွာမည်မှာ သေချာပါသလား?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ထွက်မည်",
      cancelButtonText: "မထွက်တော့ပါ",
      customClass: {
        popup: "leave-confirm-popup",
        confirmButton: "leave-confirm-confirm",
        cancelButton: "leave-confirm-cancel",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        next();
      } else {
        next(false);
      }
    });
  },

  methods: {
    pickCategory(cat) {
      this.selectedCategory = cat;
      this.selectedCard = null;
      this.result = null;
      this.isDrawing = false;
      this.spread = false;
      this.cascadeCleared = false;
      this.revealProgress = false;
      this.screen = "fan";
      this.$nextTick(() => {
        this.fanCards = this.buildFan();
        this.spreadTimeout = setTimeout(() => {
          this.spread = true;
        }, 1200);
      });
      this.cascadeTimeout = setTimeout(() => {
        this.cascadeCleared = true;
      }, 3400);
    },
    categoryIcon(id) {
      const icons = {
        1: "💞", // အချစ်ရေး
        2: "💍", // အိမ်ထောင်ရေး
        3: "💰", // ငွေကြေး
        4: "🌿", // ကျန်းမာရေး
        5: "💼", // စီးပွားရေး အလုပ်အကိုင်
        6: "✈️", // နိုင်ငံခြား
        7: "🤝", // လူမှုရေး
        8: "🎓", // ပညာရေး
      };
      return icons[id] || "✨";
    },

    categoryTint(id) {
        return `tint-${((id - 1) % 8) + 1}`;
    },

    buildFan() {
      const deck = [...TAROT_CARDS];
      for (let i = deck.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [deck[i], deck[j]] = [deck[j], deck[i]];
      }
      const isMobile = window.innerWidth <= 768;
      const spreadXVal = isMobile ? 20 : 30;
      const spreadYVal = isMobile ? 112 : 171;
      const cardW = 80;
      const cardH = 142;
      const cols = 11;
      const rows = 2;
      const stage = this.$refs.stage;
      const stageRect = stage ? stage.getBoundingClientRect() : null;

      const rawX = (col) => (col - deck.length / 4) * spreadXVal + 5;
      const rawY = (row) => (row - 4) * spreadYVal + 10;

      const xLeft = rawX(0);
      const xRight = rawX(cols - 1) + cardW;
      const fanW = xRight - xLeft;
      const yTop = rawY(0);
      const yBottom = rawY(rows - 1) + cardH;
      const fanH = yBottom - yTop;

      const targetCenterX = stageRect
        ? window.innerWidth / 2 - stageRect.left
        : (stage ? stage.offsetWidth : window.innerWidth) / 2;
      const targetCenterY = stageRect
        ? window.innerHeight / 2 - stageRect.top
        : (stage ? stage.offsetHeight : 480) / 2;

      const offsetX = targetCenterX - fanW / 2 - xLeft;
      const offsetY = targetCenterY - fanH / 2 - yTop;
      const gatherX = targetCenterX - cardW / 2;
      const gatherY = targetCenterY - cardH / 2;

      return deck.map((card, i) => {
        const row = Math.floor(i / cols);
        const col = i % cols;
        return {
          ...card,
          gatherX,
          gatherY,
          x: rawX(col) + offsetX,
          y: rawY(row) + offsetY,
          arc: (col - 5.5) * 1.5,
          stagger: i * 40,
        };
      });
    },

    selectCard(card) {
      if (this.isDrawing) return;
      this.isDrawing = true;
      this.selectedCard = card;
      this.revealProgress = false;
      this.revealTopOffset = 0;
      // Pre-render the reading now (hidden) so the long text is fully laid
      // out and painted while the reveal animation plays — it then appears
      // all at once instead of painting in progressively.
      this.result = {
        card,
        meaning: TAROT_MEANINGS[`${card.id}-${this.selectedCategory.id}`],
      };
      this.screen = "reveal";
      this.$nextTick(() => {
        const el = this.$refs.revealScreen;
        if (el) {
          const bar = this.$refs.progressBar;
          const barRect = bar
            ? bar.getBoundingClientRect()
            : el.getBoundingClientRect();
          let offset =
            window.innerHeight / 2 - (barRect.top + barRect.height / 2);
          const title = el.querySelector("h3");
          if (title) {
            const logo = document.querySelector('img[alt="Logo"]');
            const minTop = logo ? logo.getBoundingClientRect().bottom + 12 : 0;
            const titleTop = title.getBoundingClientRect().top + offset;
            if (titleTop < minTop) offset += minTop - titleTop;
          }
          this.revealTopOffset = offset;
        }
        const bar = this.$refs.progressBar;
        if (bar) void bar.offsetWidth;
        this.revealProgress = true;
      });
      this.revealTimeout = setTimeout(() => {
        this.showResult(card);
      }, 3000);
    },

    showResult(card) {
      const meaning = TAROT_MEANINGS[`${card.id}-${this.selectedCategory.id}`];
      this.result = { card, meaning };
      this.screen = "result";
    },

    back() {
      console.log("route parameter: ", this.$route.query.payment);
      this.$router.push({
        name: "withPaymenthome",
        params: {
          payment: this.$route.query.payment,
        },
      });
    },
  },

  beforeUnmount() {
    if (this.spreadTimeout) clearTimeout(this.spreadTimeout);
    if (this.cascadeTimeout) clearTimeout(this.cascadeTimeout);
    if (this.revealTimeout) clearTimeout(this.revealTimeout);
  },
};
</script>

<style>
html,
body {
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  overflow: auto;
}

body {
  padding: 30px 20px;
  scroll-behavior: smooth;
}

* {
  box-sizing: border-box;
  font-family: Arial, Helvetica, sans-serif;
}

.t-wrapper {
  color: #1f2937;
  position: relative;
}

#canvas,
#box_wrapper,
.LayoutDefault__main {
  width: 100%;
  height: 100%;
  overflow: auto;
}

div.pane-line {
  width: 150px;
  height: 5px;
  border-radius: 30px;
  background: #ff7010 !important;
}

.tarot-card {
  width: 200px;
  height: auto;
  animation-name: zoom-in;
  animation-duration: 0.3s;
  animation-timing-function: linear;
}

.mm-font {
  font-family: "Padauk", sans-serif;
  line-height: 175%;
}

.t-wrapper h3 {
  font-size: 18px;
}

.t-wrapper h5 {
  font-size: 15px;
}

.back-btn {
  font-weight: bold;
  font-size: 16px;
  background-color: #ff7010;
  color: white;
  padding: 10px 20px;
  border-radius: 30px;
  cursor: pointer;
}

.category-screen {
  position: relative;
}

.category-screen::before {
  content: "";
  position: absolute;
  top: -30px;
  left: 50%;
  width: 260px;
  height: 260px;
  transform: translateX(-50%);
  background: radial-gradient(
    circle,
    rgba(255, 112, 16, 0.12),
    transparent 70%
  );
  pointer-events: none;
}

.category-screen .pane-line {
  background: linear-gradient(90deg, #ff7010, #ffb066) !important;
  box-shadow: 0 2px 10px rgba(255, 112, 16, 0.35);
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.category-header h3 {
  margin: 0;
}

.category-star {
  color: #ff7010;
  font-size: 13px;
  opacity: 0.5;
  animation: twinkle 2.4s ease-in-out infinite;
}

.category-star-delay {
  animation-delay: 1.2s;
}

.category-card {
  position: relative;
  height: 100%;
  background: #ffffff;
  border: 1.5px solid #ffd8b8;
  border-radius: 16px;
  padding: 20px 8px 16px;
  text-align: center;
  font-size: 14.5px;
  font-weight: 600;
  color: #3a2a1c;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(255, 112, 16, 0.08);
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease, background 0.15s ease;
  animation: card-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.category-card:hover {
  border-color: #ff7010;
  box-shadow: 0 8px 20px rgba(255, 112, 16, 0.18);
  transform: translateY(-3px);
  background: #fff8f2;
}

.category-card:focus-visible {
  outline: 2px solid #ff7010;
  outline-offset: 2px;
}

.category-card:active {
  transform: scale(0.96) translateY(0);
  background: #fff3e6;
  box-shadow: 0 2px 6px rgba(255, 112, 16, 0.15);
}

.category-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
}

.category-label {
  line-height: 1.3;
}

.tint-1 { background: linear-gradient(145deg, #ffe3ec, #ffc9dc); } /* love */
.tint-2 { background: linear-gradient(145deg, #f1e6ff, #ddc7ff); } /* marriage */
.tint-3 { background: linear-gradient(145deg, #fff3c9, #ffe08a); } /* money */
.tint-4 { background: linear-gradient(145deg, #e3f7e5, #b9ecc0); } /* health */
.tint-5 { background: linear-gradient(145deg, #e3edff, #c2d9ff); } /* career */
.tint-6 { background: linear-gradient(145deg, #e0f6ff, #b8e8ff); } /* foreign */
.tint-7 { background: linear-gradient(145deg, #fff0e0, #ffd7ad); } /* social */
.tint-8 { background: linear-gradient(145deg, #eae6ff, #cfc4ff); } /* education */

@keyframes card-rise {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes twinkle {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1.15);
  }
}

.result-text {
  font-size: 16px;
}

.result-label {
  color: #ff7010 !important;
  font-weight: bold;
  border-bottom: 1px solid #ff7010;
  padding-bottom: 2px;
}

.result-screen {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
}

.result-screen--active {
  position: static;
  visibility: visible;
  opacity: 1;
  pointer-events: auto;
}

.tarot-stage {
  position: relative;
  width: 100%;
  height: calc(100vh - 170px);
  min-height: 420px;
  margin-top: 6px;
}

.tarot-card-wrap {
  position: absolute;
  width: 80px;
  height: 142px;
  cursor: pointer;
  background: #14212e;
  border-radius: 8px;
  perspective: 800px;
  transition: left 1.2s cubic-bezier(0.22, 1, 0.36, 1),
    top 1.2s cubic-bezier(0.22, 1, 0.36, 1), transform 0.6s ease;
}

.tarot-card-wrap.shuffling {
  animation: shuffle-jitter 0.25s ease-in-out 2;
  animation-delay: 0.5s;
}

.tarot-card-deal {
  position: relative;
  width: 100%;
  height: 100%;
}

.tarot-card-wrap.dealing .tarot-card-deal {
  animation: deal-spin 0.9s ease-out both;
}

.tarot-card-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.reveal-card {
  width: 150px;
  height: 267px;
  background: #14212e;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  animation-name: zoom-in;
  animation-duration: 0.3s;
  animation-timing-function: linear;
}

@media (max-height: 740px) {
  .reveal-card {
    width: 128px;
    height: 228px;
  }

  .reveal-screen h3 {
    margin-bottom: 8px;
  }

  .reveal-hint {
    margin-top: 10px;
  }

  .reveal-progress {
    margin-top: 14px;
  }
}

.reveal-screen {
  min-height: calc(100vh - 170px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.reveal-card img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.reveal-hint {
  font-size: 15px;
  color: #6b7280;
  margin-top: 16px;
  margin-bottom: 0;
}

.reveal-progress {
  width: 220px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 30px;
  overflow: hidden;
  margin-top: 18px;
}

.reveal-progress-bar {
  width: 0%;
  height: 100%;
  background: #ff7010;
  border-radius: 30px;
  transition: width 5s linear;
}

.reveal-progress-bar.active {
  width: 100%;
}

@keyframes zoom-in {
  0% {
    transform: scale(0, 0);
  }

  100% {
    transform: scale(1, 1);
  }
}

@keyframes shuffle-jitter {
  0%,
  100% {
    transform: translate(0, 0) rotate(0deg);
  }

  25% {
    transform: translate(-4px, -2px) rotate(-2deg);
  }

  50% {
    transform: translate(4px, 1px) rotate(2deg);
  }

  75% {
    transform: translate(-3px, 2px) rotate(-1deg);
  }
}

@keyframes deal-spin {
  0% {
    transform: rotateY(0deg);
  }

  100% {
    transform: rotateY(360deg);
  }
}
</style>
